import {
  EuiBadge,
  EuiCallOut,
  EuiDescriptionList,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { CreateQuestionnaireSummaryProps } from "src/questionnaires/types";
import { CreateNewOrganisation } from "../organisation/createNew";
import { CreateNewUser } from "../members/createNew";

export const CreateQuestionnaireSummary = ({
  displayName,
  members,
  newOrganizations,
  newUsers,
  organizations,
  selectedQuestionnaires,
}: CreateQuestionnaireSummaryProps): JSX.Element => {
  const { translate } = useAnketaI18n();

  const hasNewOrganizations = newOrganizations.length > 0;
  const hasNewUsers = newUsers.length > 0;
  const hasExistingOrganizations = organizations.length > 0;
  const hasMembers = members.length > 0;

  return (
    <EuiForm component="div">
      <EuiTitle size="xs">
        <h3 style={{ marginBottom: 8 }}>{translate("anketa.organization")}</h3>
      </EuiTitle>

      {hasExistingOrganizations ? (
        <EuiPanel color="subdued" paddingSize="m">
          <EuiDescriptionList
            listItems={organizations.map((org) => ({
              title: org.label,
              description: (
                <EuiFlexGroup wrap gutterSize="xs" style={{ marginTop: 4 }}>
                  {org.value.domains?.map((domain, i) => (
                    <EuiFlexItem
                      key={`${org.value.alias}-${domain.name}-${i}`}
                      grow={false}
                    >
                      <EuiBadge color="success">@{domain.name}</EuiBadge>
                    </EuiFlexItem>
                  )) ?? (
                    <EuiText size="xs" color="subdued">
                      <span>
                        {translate("anketa.domains.none") || "No domains"}
                      </span>
                    </EuiText>
                  )}
                </EuiFlexGroup>
              ),
            }))}
          />
        </EuiPanel>
      ) : (
        <EuiText size="s" color="subdued">
          <p>
            {translate("anketa.organizations.none") ||
              "No existing organizations selected."}
          </p>
        </EuiText>
      )}

      {hasNewOrganizations && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut
            title={translate("anketa.create.summary", {
              values: { elements: translate("anketa.organization") },
            })}
            color="warning"
            iconType="help"
            size="s"
          />
          <EuiSpacer size="s" />
          <EuiFlexGroup direction="column" gutterSize="s">
            {newOrganizations.map((newOrganization) => (
              <EuiFlexItem key={newOrganization.label} grow={false}>
                <CreateNewOrganisation
                  name={newOrganization.label}
                  orgDomains={
                    newOrganization.value ? newOrganization.value.domains : []
                  }
                />
              </EuiFlexItem>
            ))}
          </EuiFlexGroup>
        </>
      )}

      <EuiSpacer size="l" />

      <EuiTitle size="xs">
        <h3 style={{ marginBottom: 8 }}>{translate("anketa.members")}</h3>
      </EuiTitle>

      {hasMembers ? (
        <EuiPanel color="subdued" paddingSize="m">
          <EuiDescriptionList
            listItems={members.map((member) => ({
              title: member.value.email,
              description: member.value.username
                ? `${member.value.username} (${member.value.firstName} ${member.value.lastName})`
                : translate("anketa.members.created.invited"),
            }))}
          />
        </EuiPanel>
      ) : (
        <EuiText size="s" color="subdued">
          <p>
            {translate("anketa.members.none") ||
              "No existing members selected."}
          </p>
        </EuiText>
      )}

      {hasNewUsers && (
        <>
          <EuiSpacer size="s" />
          <EuiCallOut
            title={translate("anketa.create.summary", {
              values: { elements: translate("anketa.members") },
            })}
            color="warning"
            iconType="help"
            size="s"
          />
          <EuiSpacer size="s" />
          <EuiFlexGroup direction="column" gutterSize="s">
            {newUsers.map((user) => (
              <EuiFlexItem key={user.label}>
                <CreateNewUser
                  email={user.value.email}
                  firstName={user.value.firstName}
                  lastName={user.value.lastName}
                  organizations={user.value.organizations}
                />
              </EuiFlexItem>
            ))}
          </EuiFlexGroup>
        </>
      )}

      <EuiSpacer size="l" />

      <EuiTitle size="xs">
        <h3 style={{ marginBottom: 8 }}>{translate("anketa.questionnaire")}</h3>
      </EuiTitle>

      <EuiPanel color="subdued" paddingSize="m">
        <EuiDescriptionList
          listItems={[
            {
              title: translate("anketa.questionnaire.template") || "Template",
              description:
                selectedQuestionnaires && selectedQuestionnaires.length > 0
                  ? selectedQuestionnaires[0].label
                  : "-",
            },
            {
              title: translate("anketa.displayName"),
              description: displayName || "-",
            },
          ]}
        />
      </EuiPanel>
    </EuiForm>
  );
};
